/*========================================================================
 * $Id$
 *
 * Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2020
 *
 * Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
 * Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
 * Urheber festgelegten Stelle in der Funktion des Computerprogramms
 * angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
 * Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
 * Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
 * =========================================================================*/

define([
  "jquery",
  "underscore",
  "service/ajax_channel",
  "service/channel",
  "saveAs",
  "utils/login-window-handler",
  "utils/browser-detect"
], function(
  $,
  _,
  AjaxChannel,
  Channel,
  saveAs,
  LoginWindowHandler,
  BrowserDetect
) {

  var MAX_REQUEST_COUNT = 1;
  var loginWindowHandler = new LoginWindowHandler();

  var CrossDomainAjaxChannel = AjaxChannel.extend({
    internalCall: function(path, request, responseObserver, formatOptions, headers, options, requestCount, promise) {
      requestCount = requestCount || 0;
      
      // crossDomain ajax attribute is available from jQuery 1.5
      if ($ && $.support && !$.support.cors) {
        $.support.cors = true;
      }
    
      var that = this,
          promise = promise || that.createDeferredCallFinishedPromise(this, options);
        
      var loginAsError = this.options && this.options.loginAsError;

      var isBlobRequestType = formatOptions && formatOptions.responseType === 'blob';
      var dataType = formatOptions && formatOptions.dataType || 'json';
      var requestData = request ? (/json/i.test(dataType) || isBlobRequestType ? JSON.stringify(request) : request) : null;
      
      if(options && options.xhrFields) {
        this.setXhrFields(options.xhrFields);
      }

      // Blob dataType not suported in jQuery ajax for this version
      if (isBlobRequestType) {
        dataType = "blob";
        this.supportAjaxBinaryResponseType();
      }

      // X-Requested-With is added on jQuery v1.5, now we have to add this manually 
      headers = _.extend(
        headers || {},
        options && options.headers || {},
        {'X-Requested-With': 'XMLHttpRequest'}
      );

      var ajaxRequest = $.ajax({
        beforeSend: function(jqXHR, settings) {
          jqXHR.originalUrl = settings.url;
          jqXHR.serviceURL = that.get("url");
          if (_.isFunction(options.beforeSend)) {
            options.beforeSend(jqXHR, settings);
          }
        },
        url: this.get("url") + (path || ""),
        type: options && options.method || "post",
        crossDomain: options && options.crossDomain || true,
        xhrFields: this.xhrFields,
        headers: headers || {},
        dataType: dataType,
        data: requestData,
        context: this,
        timeout: this.getAjaxTimeout(options, path)
      }).done(function(data, textStatus, jqXHR) { // success
          that.success(data, promise, responseObserver, jqXHR, formatOptions, options);
      }).fail(function(jqXHR, textStatus, errorThrown) { // failure

        var retryCallRequest = function(newHeaders) {
          newHeaders = _.extend(headers, newHeaders || {});

          that.internalCall(path, request, responseObserver, formatOptions, newHeaders, options, requestCount + 1, promise);
        };

        var triggerApplicationError = function (error) {
          that.error(
            { status: jqXHR.status, message: textStatus, url: that.get("url"), error: error, path: path },
            promise,
            responseObserver
          );
        }
        
        if (this.shouldHandleAuthWithBrowserWindow(jqXHR, requestCount)) {
          this.handleAuthenticationError(retryCallRequest, jqXHR, requestCount, triggerApplicationError);
        } else {
          // TODO remove this part when Fabasoft uses 403 resposne status instead of 401.
          var treat403As401 = that.options && that.options.crossDomainAjax && that.options.crossDomainAjax.onLoginAsErrorTreat403as401;
          if (jqXHR.status === 403 && loginAsError && treat403As401) {
            jqXHR.status = 401;
          }

          var defaultHandler = function () {
            triggerApplicationError(errorThrown);
          };

          this.handleRequestFailed(retryCallRequest, jqXHR, requestCount, defaultHandler, triggerApplicationError);
        }
      });

      if (options.onJqHhrChange) {
        options.onJqHhrChange(request);
      }
      
      return promise;
    },

    success: function (data, promise, responseObserver, jqXHR, formatOptions, options) {
      loginWindowHandler.retryNextServiceRequestInQueue(jqXHR.serviceURL);
      if (this.isBlobExportResponse(formatOptions, jqXHR)) {
        this.saveExportResultToFile(data, jqXHR);
      }
      
      AjaxChannel.prototype.success.apply(this, [data, promise, responseObserver, options]);
    },

    isBlobExportResponse: function(formatOptions, jqXHR) {
      return formatOptions && formatOptions.responseType === 'blob' && jqXHR.getResponseHeader('Content-Disposition');
    },

    handleAuthenticationError: function (retryCallRequest, jqXHR, retryCount, triggerApplicationError) {
      var that = this;
      var defaultHandler = function () {
        loginWindowHandler.resetServiceLoginWindow(that.get("url"), that.options);
        loginWindowHandler.openLoginWindow(retryCallRequest, jqXHR.originalUrl, that.get("url"), that.options, retryCount);
      };
      
      var onRequestFailed = this.applicationOnRequestFailedProp(this.options);
      if (onRequestFailed !== false) {
        var errorState = { 
          status: jqXHR.status,
          reason: 'Authentication',
          retryCount: retryCount
        };

        var requestInfo = {
          requestUrl: jqXHR.originalUrl,
          loginUrl: loginWindowHandler.getLoginHandlerFullURL(this.get("url"), this.options),
          serviceUrl: this.get("url")
        };

        this.triggerOnRequestFailed(errorState, requestInfo, retryCallRequest, defaultHandler, onRequestFailed, triggerApplicationError);
      } else {
        defaultHandler();
      }
    },

    triggerOnRequestFailed: function (errorState, requestInfo, retryCallRequest, defaultHandler, onRequestFailed, triggerApplicationError) {
      try {
        onRequestFailed(errorState, requestInfo, retryCallRequest, defaultHandler);
      } catch (error) {
        console.warn('Unexpected error occurred inside "onRequestFailed" application property callback', error);
        triggerApplicationError(error);
      }
    },

    handleRequestFailed: function (retryCallRequest, jqXHR, retryCount, defaultHandler, triggerApplicationError) {
      var onRequestFailed = this.applicationOnRequestFailedProp(this.options);
      if (onRequestFailed !== false) {
        var errorState = { 
          status: jqXHR.status,
          reason: 'RequestFail',
          retryCount: retryCount
        };

        var requestInfo = {
          requestUrl: jqXHR.originalUrl,
          loginUrl: loginWindowHandler.getLoginHandlerFullURL(this.get("url"), this.options),
          serviceUrl: this.get("url")
        };

        this.triggerOnRequestFailed(errorState, requestInfo, retryCallRequest, defaultHandler, onRequestFailed, triggerApplicationError);
      } else {
        defaultHandler();
      }
    },

    applicationOnRequestFailedProp: function (channelOptions) {
      var onRequestFailed = channelOptions &&
      channelOptions.application &&
      channelOptions.application.options &&
      channelOptions.application.options.onRequestFailed;
      if (onRequestFailed && _.isFunction(onRequestFailed)) {
        return onRequestFailed;
      }

      return false;
    },

    saveExportResultToFile: function(data, jqXHR) {
      var dispositionHeader = jqXHR.getResponseHeader('Content-Disposition');
      var filename = dispositionHeader.slice(_.indexOf(dispositionHeader, "=") + 1, dispositionHeader.length);

      if (typeof saveAs !== 'undefined') {
        saveAs(data, filename);
      } else if (typeof window.navigator.msSaveBlob !== 'undefined') {
        // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for
        // which they were created These URLs will no longer resolve as the data backing the 
        //  URL has been freed."
        var type = jqXHR.getResponseHeader('Content-Type');
        var blob = new Blob([jqXHR.responseText], { type: type });
        window.navigator.msSaveBlob(blob, filename);
      }
    },

    shouldHandleAuthWithBrowserWindow: function (jqXHR, requestCount) {
      var loginAsError = this.options && this.options.loginAsError;

      var treatBasicAuth401as403OnCurrentBrowser = this.options &&
        this.options.crossDomainAjax &&
        this.options.crossDomainAjax.treatBasicAuth401as403ForBrowser(BrowserDetect.detectCurrentBrowserName());

      var treatBasicAuthAs403ForBrowser =  
        jqXHR.getResponseHeader('www-authenticate') !== null && 
        jqXHR.status === 401 &&
        treatBasicAuth401as403OnCurrentBrowser;

        return ( jqXHR.status === 403 || treatBasicAuthAs403ForBrowser ) && 
          requestCount < MAX_REQUEST_COUNT 
          && !loginAsError;
    },

    supportAjaxBinaryResponseType: function() {
      $.ajaxTransport("+*", function(options, originalOptions, jqXHR){
        // Test for the conditions that mean we can/want to send/receive blobs or arraybuffers - we need XMLHttpRequest
        // level 2 (so feature-detect against window.FormData), feature detect against window.Blob or window.ArrayBuffer,
        // and then check to see if the dataType is blob/arraybuffer or the data itself is a Blob/ArrayBuffer
        var isBlobOrArrayBufferDataType = options.dataType && (options.dataType == 'blob' || options.dataType == 'arraybuffer');
        var isDataBlobInstance = options.data && window.Blob && options.data instanceof Blob;
        if (window.FormData && (isBlobOrArrayBufferDataType || isDataBlobInstance))
        {
          var xhr;

          return {
              /**
               * Return a transport capable of sending and/or receiving blobs - in this case, we instantiate
               * a new XMLHttpRequest and use it to actually perform the request, and funnel the result back
               * into the jquery complete callback (such as the success function, done blocks, etc.)
               *
               * @param headers
               * @param completeCallback
               */
              send: function(headers, completeCallback){
                  var url = options.url || window.location.href,
                      type = options.type || 'GET',
                      dataType = options.dataType || 'text',
                      data = options.data || null,
                      async = options.async || true;

                  xhr = new XMLHttpRequest();
                  xhr.addEventListener('load', function(){
                      var res = {},
                          success = xhr.status >= 200 && xhr.status < 300 || xhr.status === 304;

                      if (success){
                          res[dataType] = xhr.response;
                      } else {
                          res.text = xhr.statusText;
                      }

                      completeCallback(xhr.status, xhr.statusText, res, xhr.getAllResponseHeaders());
                  });

                  xhr.open(type, url, async);
                  xhr.responseType = dataType;

                  for (var key in options.xhrFields){
                    xhr[key] = options.xhrFields[key];
                  }

                  for (var key in headers){
                    if (headers.hasOwnProperty(key)){
                        xhr.setRequestHeader(key, headers[key]);
                    }
                  }

                  xhr.send(data);
              },
              abort: function(){
                  if (xhr){
                      xhr.abort();
                  }
              }
          };
        }
      });
    }
  });

  return CrossDomainAjaxChannel;
});

